/**
 * انگشت نگاری سبک دستگاه — کاملاً client-side، بدون هیچ دادهٔ شخصی
 *
 * یک شناسهٔ پایدار در localStorage نگه داشته می‌شود؛ اگر localStorage پاک شده
 * باشد (یا حالت ناشناس) از اثر انگشت سبک (رزولوشن، زبان، منطقهٔ زمانی، هسته‌ها،
 * حافظه، لمسی بودن، پلتفرم) یک شناسهٔ کوتاه می‌سازیم تا دستگاه دوباره شناسایی
 * شود. برای پایداری بیشتر، بخشی از canvas fingerprint هم در محاسبه هست.
 */

const CLIENT_ID_KEY = 'elona_client_id'

/** تبدیل رشته به یک هش ۳۲ بیتی (FNV-1a) به‌صورت hex */
function hash32(input: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

/** شناسهٔ تصادفی ۳۲ کاراکتری */
function randomId(): string {
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** اثر انگشت سبک دستگاه — فقط مشخصات فنی، بدون دادهٔ شخصی */
export function getDeviceFingerprint(): string {
  const nav = navigator as Navigator & { deviceMemory?: number; hardwareConcurrency?: number }
  const parts: string[] = [
    String(screen?.width ?? 0),
    String(screen?.height ?? 0),
    String(screen?.colorDepth ?? 0),
    String(window.devicePixelRatio ?? 0),
    nav.language ?? '',
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
    String(nav.hardwareConcurrency ?? 0),
    String(nav.deviceMemory ?? 0),
    String(nav.maxTouchPoints ?? 0),
    nav.platform ?? '',
  ]

  // Canvas fingerprint — رسم ساده و هش خروجی (فقط مشخصات رندر GPU/فونت)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 240
    canvas.height = 60
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'alphabetic'
      ctx.font = "16px 'Vazirmatn', sans-serif"
      ctx.fillStyle = '#e25f48'
      ctx.fillRect(4, 4, 60, 24)
      ctx.fillStyle = '#211b16'
      ctx.fillText('الونا · Elona ۱۲۳', 8, 28)
      ctx.strokeStyle = 'rgba(201,143,43,0.7)'
      ctx.beginPath()
      ctx.arc(160, 30, 22, 0, Math.PI * 1.5)
      ctx.stroke()
      parts.push(hash32(canvas.toDataURL()))
    }
  } catch {
    // canvas در دسترس نیست — بی‌خیال
  }

  return parts.map(hash32).join('').slice(0, 32)
}

/**
 * شناسهٔ پایدار دستگاه: اول localStorage، اگر نبود fingerprint و اگر آن هم
 * شکست خورد یک شناسهٔ تصادفی (برای همان نشست).
 */
export function getClientId(): string {
  try {
    const stored = localStorage.getItem(CLIENT_ID_KEY)
    if (stored) return stored
  } catch {
    // localStorage در دسترس نیست (حالت محدود)
  }

  let id = ''
  try {
    id = getDeviceFingerprint()
  } catch {
    id = ''
  }
  if (!id || id === '00000000000000000000000000000000') {
    id = randomId()
  }

  try {
    localStorage.setItem(CLIENT_ID_KEY, id)
  } catch {
    // ذخیره ممکن نیست — همین شناسه برای این نشست استفاده می‌شود
  }
  return id
}
